import React, { useCallback, useMemo } from "react";

import { formatAudioTime } from "@korben/audio-player";
import { faMicrophone } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Badge, Icon } from "@orgmym/leeloo-core";
import clsx from "clsx";
import { useTranslation } from "react-i18next";

import type { Chat } from "../../types/chat/chat";
import { formatLastActivityDate } from "../../utils/date.utils";
import { ChatAvatar } from "../chat-avatar";
import * as styles from "./chat-list-item.css";

type BadgeConfig = {
  labelKey: string;
  fallback: string;
  variant: "brand" | "danger";
  testId: string;
};

interface ChatListItemProps {
  chat: Chat;
  isSelected: boolean;
  onSelect: (chat: Chat) => void;
  activeTab?: string;
}

export const ChatListItem: React.FC<ChatListItemProps> = ({
  chat,
  isSelected,
  onSelect,
  activeTab
}) => {
  const { t, i18n } = useTranslation();
  const audioMedia = chat.last_message?.attachment?.media?.find(
    (media) =>
      media.type === "audio" || Boolean(media.mime_type?.startsWith("audio/"))
  );
  const isVoiceMessage =
    chat.last_message?.media_type === "audio" || Boolean(audioMedia);
  const voiceDuration =
    audioMedia?.duration ??
    chat.last_message?.attachment?.duration ??
    chat.last_message?.duration ??
    0;

  const badges = useMemo<BadgeConfig[]>(() => {
    const badgeList: BadgeConfig[] = [];

    if (chat.media_request != null && chat.media_request > 0) {
      badgeList.push({
        labelKey: "creators_messaging_messages_list_media_request_icon",
        fallback: "Media request",
        variant: "brand",
        testId: "pending-request"
      });
    }

    if (chat.to_deliver != null && chat.to_deliver > 0) {
      badgeList.push({
        labelKey: "creators_messaging_chat_header_deliver_cta",
        fallback: "To deliver",
        variant: "danger",
        testId: "to-deliver"
      });
    }

    if (badgeList.length === 0 && activeTab && activeTab !== "all") {
      if (activeTab === "pending_request") {
        badgeList.push({
          labelKey: "creators_messaging_messages_list_media_request_icon",
          fallback: "Media request",
          variant: "brand",
          testId: "pending-request"
        });
      } else if (activeTab === "to_deliver") {
        badgeList.push({
          labelKey: "creators_messaging_chat_header_deliver_cta",
          fallback: "To deliver",
          variant: "danger",
          testId: "to-deliver"
        });
      }
    }

    return badgeList;
  }, [chat.media_request, chat.to_deliver, activeTab, chat.id]);

  const handleClick = useCallback(() => {
    onSelect(chat);
  }, [chat, onSelect]);

  return (
    <div
      className={clsx(
        styles.item,
        isSelected && styles.itemSelected,
        !chat.is_read && styles.itemUnread
      )}
      onClick={handleClick}
      data-testid={`chat-item-${chat.id}`}
      role="button"
      tabIndex={0}
    >
      <ChatAvatar
        username={chat.user.username}
        profilePicture={chat.user.avatar_url}
        isOnline={chat.user.is_online}
        className={styles.itemAvatar}
        data-testid={`chat-item-avatar-${chat.id}`}
      />

      <div
        className={styles.itemContent}
        data-testid={`chat-item-content-${chat.id}`}
      >
        <div className={styles.itemHeader}>
          <div className={styles.itemUsernameWrapper}>
            <h3
              className={styles.itemUsername}
              data-testid={`chat-item-username-${chat.id}`}
            >
              {chat.user.username}
            </h3>
            {chat.user.is_certified && (
              <Icon
                name="certification"
                data-testid={`chat-item-certified-${chat.id}`}
              />
            )}
            {chat.user.nickname && badges.length > 0 && (
              <span
                className={styles.itemNickname}
                data-testid={`chat-item-nickname-${chat.id}`}
              >
                {chat.user.nickname}
              </span>
            )}
          </div>
          <span
            className={styles.itemTime({ unread: !chat.is_read })}
            data-testid={`chat-item-time-${chat.id}`}
          >
            {chat.last_message?.date &&
              formatLastActivityDate(chat.last_message.date, i18n.language)}
          </span>
        </div>

        {(isVoiceMessage ||
          badges.length > 0 ||
          chat.user.nickname ||
          !chat.is_read) && (
          <div className={styles.itemFooter}>
            <div
              className={styles.itemBadges}
              data-testid={`chat-item-badges-${chat.id}`}
            >
              {isVoiceMessage ? (
                <span
                  className={styles.voicePreview}
                  data-testid={`chat-item-voice-preview-${chat.id}`}
                >
                  <FontAwesomeIcon icon={faMicrophone} aria-hidden="true" />
                  vocal · {formatAudioTime(voiceDuration)}
                </span>
              ) : badges.length > 0 ? (
                badges.map((badge) => (
                  <Badge
                    key={badge.testId}
                    variant={badge.variant}
                    rounded
                    data-testid={`chat-item-badge-${badge.testId}-${chat.id}`}
                  >
                    {t(badge.labelKey, badge.fallback)}
                  </Badge>
                ))
              ) : (
                chat.user.nickname && (
                  <span
                    className={styles.itemNickname}
                    data-testid={`chat-item-preview-${chat.id}`}
                  >
                    {chat.user.nickname}
                  </span>
                )
              )}
            </div>
            <div className={styles.itemStatusIcons}>
              {chat.user.is_blacklisted && (
                <Icon
                  name="blacklisted"
                  size="small"
                  className={styles.itemStatusIconBlacklisted}
                  data-testid={`chat-item-blacklisted-${chat.id}`}
                />
              )}
              {chat.is_enabled === false && (
                <Icon
                  name="mute-message"
                  data-testid={`chat-item-messaging-off-${chat.id}`}
                />
              )}
              {chat.user.is_muted && (
                <Icon
                  name="notifications-off"
                  data-testid={`chat-item-notifications-off-${chat.id}`}
                />
              )}
              {!chat.is_read && (
                <div
                  className={styles.unreadIndicator({
                    grey:
                      !chat.is_read && (!chat.is_enabled || chat.user.is_muted)
                  })}
                ></div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ChatListItem;
